/*
 * relay-client.js  (ES5 + jQuery 3, load after terminal-pos.js)
 * General messages between the POS devices of one organizer, over the shared POS socket.
 * Always on while the POS channel is open (any SocketHub.ensurePos / initPos call), so every
 * connected POS is reachable. The server stamps the sender; nobody can pose as another device.
 *
 * Topics are free-form names ([A-Za-z0-9_.:-], max 64), e.g. 'cart.transfer', 'table.lock',
 * 'chat'. Adding a topic needs no server change.
 *
 * ---- Send ------------------------------------------------------------------------------
 *   SocketHub.relay.send(deviceId, topic, data)      -> promise .done({msgId, count:1})
 *   SocketHub.relay.broadcast(topic, data)           -> promise .done({msgId, count})  all OTHER POS devices
 *   SocketHub.relay.request(deviceId, topic, data, {timeout})  -> promise
 *       .done({data, from, msgId})   the other device's handler answered with success
 *       .fail(err)  err = {code, reason, data, msgId, to, topic, uncertain}
 *     codes: not_initialized | not_enabled | channel_offline | bad_request | too_large | send_failed
 *            device_offline (never delivered) | rate_limited (never delivered)
 *            failed | rejected (the other side answered; reason e.g. no_handler, handler_error)
 *            timeout | channel_offline with uncertain:true (it MAY have arrived)
 *   Delivery is at-most-once: nothing is resent automatically. For work that must run
 *   exactly once (printing), use SocketHub.main (terminal-main.js) or make the handler idempotent.
 *   Every promise carries .msgId.
 *
 * ---- Receive ---------------------------------------------------------------------------
 *   SocketHub.relay.on(topic, function (data, ctx) { ... })      one handler per topic
 *     ctx: {msgId, from, topic, broadcast, expectReply}
 *     For a request() the return value is the answer:
 *       nothing / true -> success   false -> failed   {status, data, reason} -> as given
 *       any other value -> success with that value as data   a promise -> its result
 *     A request for a topic without a handler is answered 'rejected' / 'no_handler'.
 *   SocketHub.relay.off(topic)
 *   jQuery event 'relay:message' {msgId, from, topic, data, broadcast, expectReply}
 *     fires for EVERY incoming message (also when a handler exists), for page code that prefers events.
 *
 * ---- Presence --------------------------------------------------------------------------
 *   SocketHub.relay.devices()   ids of the other POS devices online now
 *   SocketHub.relay.isOnline(id)   SocketHub.relay.self()   (this device's id)
 *   jQuery event 'relay:presence' {id, online, devices}   (id null = full list after (re)connect)
 *
 * Example:
 *   // device A: hand a cart to device B and wait until B took it
 *   SocketHub.relay.request('POS-2', 'cart.transfer', { cart: locationCart })
 *     .done(function () { emptyAllCart('transferred'); })
 *     .fail(function (e) { showToast({ type: 'error', message: e.uncertain ? 'Ukjent status' : 'Overføring feilet' }); });
 *   // device B
 *   SocketHub.relay.on('cart.transfer', function (d, ctx) { return loadCartFrom(d.cart, ctx.from); });
 *   // anyone: tell every POS that a table changed
 *   SocketHub.relay.broadcast('table.changed', { tableId: 12 });
 */
(function (window, $) {
  'use strict';
  var T = window.SocketHub,
    M = T.MSG;
  var SENT_TIMEOUT = 8000,
    REQUEST_TIMEOUT = 10000,
    MAX_DATA = 200000; // server limit is 256 KB per frame including the envelope
  var TOPIC = /^[A-Za-z0-9_.:-]{1,64}$/;
  var STATUSES = { success: true, failed: true, rejected: true };

  var selfId = null,
    devices = {}, // id -> true
    pending = {}, // msgId -> {msgId, to, topic, d, expectReply, delivered, timeout, timer}
    handlers = {}; // topic -> fn

  function fire(name, data) {
    $(document).trigger('relay:' + name, [data]);
  }
  function deviceList() {
    return $.map(devices, function (v, id) {
      return id;
    });
  }
  function err(code, extra) {
    return $.extend(
      {
        code: code,
        reason: null,
        data: null,
        msgId: null,
        to: null,
        topic: null,
        uncertain: false,
      },
      extra
    );
  }

  // ======================================================================================
  // sending
  // ======================================================================================
  function settle(p, ok, value) {
    if (pending[p.msgId] !== p) return;
    delete pending[p.msgId];
    clearTimeout(p.timer);
    if (ok) p.d.resolve(value);
    else p.d.reject(value);
  }

  function failP(p, code, extra) {
    settle(
      p,
      false,
      err(code, $.extend({ msgId: p.msgId, to: p.to, topic: p.topic }, extra))
    );
  }

  function arm(p, ms) {
    clearTimeout(p.timer);
    p.timer = setTimeout(function () {
      failP(p, 'timeout', { uncertain: true });
    }, ms);
  }

  function out(to, topic, data, expectReply, timeout) {
    var d = $.Deferred(),
      pr = d.promise();
    function no(code) {
      d.reject(err(code, { to: to, topic: topic }));
      return pr;
    }
    if (!T.pos || !T.pos.started()) return no('not_initialized');
    if (!T._posHas('relay')) return no('not_enabled');
    if (!T._posReady()) return no('channel_offline');
    if (typeof topic !== 'string' || !TOPIC.test(topic))
      return no('bad_request');
    if (to !== null) {
      to = String(to);
      if (!to || to === selfId) return no('bad_request');
      if (!devices[to]) return no('device_offline');
    }
    if (data === undefined) data = null;
    try {
      if (JSON.stringify(data).length > MAX_DATA) return no('too_large');
    } catch (e) {
      return no('bad_request');
    }

    var p = {
      msgId: T.newId(),
      to: to,
      topic: topic,
      d: d,
      expectReply: !!expectReply,
      delivered: false,
      timeout: timeout || REQUEST_TIMEOUT,
      timer: null,
    };
    pr.msgId = p.msgId;
    pending[p.msgId] = p;

    var payload = { msgId: p.msgId, topic: topic, data: data };
    if (to !== null) payload.to = to;
    if (p.expectReply) payload.expectReply = true;
    if (!T._posSend(M.RELAY_SEND, payload)) {
      delete pending[p.msgId];
      return no('send_failed');
    }
    arm(p, SENT_TIMEOUT); // the server confirms delivery with relay.sent
    return pr;
  }

  // ======================================================================================
  // receiving
  // ======================================================================================
  function normalize(v) {
    if (v === undefined || v === null || v === true)
      return { status: 'success', reason: null, data: null };
    if (v === false) return { status: 'failed', reason: null, data: null };
    if (typeof v === 'object' && typeof v.status === 'string')
      return {
        status: STATUSES[v.status] ? v.status : 'failed',
        reason: v.reason || null,
        data: v.data === undefined ? null : v.data,
      };
    return { status: 'success', reason: null, data: v };
  }

  function reply(m, res) {
    T._posSend(M.RELAY_REPLY, {
      msgId: m.msgId,
      to: m.from,
      status: res.status,
      reason: res.reason || null,
      data: res.data === undefined ? null : res.data,
    });
  }

  function onIncoming(m) {
    if (typeof m.msgId !== 'string' || typeof m.from !== 'string') return;
    var data = m.data === undefined ? null : m.data;
    var ctx = {
      msgId: m.msgId,
      from: m.from,
      topic: m.topic,
      broadcast: !!m.broadcast,
      expectReply: !!m.expectReply,
    };
    fire('message', $.extend({ data: data }, ctx));

    var h = handlers[m.topic];
    if (!ctx.expectReply) {
      if (typeof h === 'function') {
        try {
          h(data, ctx);
        } catch (e) {
          console.error('[relay] handler for ' + m.topic + ' threw', e);
        }
      }
      return;
    }

    if (typeof h !== 'function') {
      reply(m, { status: 'rejected', reason: 'no_handler', data: null });
      return;
    }
    var res;
    try {
      res = h(data, ctx);
    } catch (e) {
      console.error('[relay] handler for ' + m.topic + ' threw', e);
      reply(m, { status: 'failed', reason: 'handler_error', data: null });
      return;
    }
    $.when(res).then(
      function (v) {
        reply(m, normalize(v));
      },
      function (e) {
        reply(m, {
          status: 'failed',
          reason:
            (e && typeof e.reason === 'string' && e.reason) ||
            (typeof e === 'string' ? e : 'handler_error'),
          data: null,
        });
      }
    );
  }

  // ======================================================================================
  // socket module
  // ======================================================================================
  function pendingList() {
    return $.map(pending, function (p) {
      return p;
    });
  }

  T._posModule('relay', {
    onReady: function (w) {
      selfId = w && w.id ? String(w.id) : null;
      devices = {};
      $.each((w && w.devices) || [], function (i, id) {
        devices[String(id)] = true;
      });
      fire('presence', { id: null, online: true, devices: deviceList() });
    },
    onMessage: function (m) {
      var p = m.msgId ? pending[m.msgId] : null;
      switch (m.type) {
        case M.RELAY_MESSAGE:
          onIncoming(m);
          break;
        case M.RELAY_SENT:
          if (!p) break;
          if (p.expectReply) {
            p.delivered = true;
            arm(p, p.timeout); // now wait for the answer
          } else {
            settle(p, true, { msgId: p.msgId, count: m.count || 0 });
          }
          break;
        case M.RELAY_REPLY:
          if (!p || !p.expectReply || m.from !== p.to) break;
          if (m.status === 'success')
            settle(p, true, {
              data: m.data === undefined ? null : m.data,
              from: m.from,
              msgId: m.msgId,
            });
          else
            failP(p, STATUSES[m.status] ? m.status : 'failed', {
              reason: m.reason || null,
              data: m.data === undefined ? null : m.data,
            });
          break;
        case M.RELAY_ERROR:
          if (!p) break;
          if (m.code === 'device_offline' && p.to) delete devices[p.to];
          failP(p, m.code || 'failed');
          break;
        case M.RELAY_PRESENCE:
          if (typeof m.id !== 'string') break;
          if (m.online) devices[m.id] = true;
          else delete devices[m.id];
          fire('presence', {
            id: m.id,
            online: !!m.online,
            devices: deviceList(),
          });
          break;
      }
    },
    onClose: function () {
      devices = {};
      fire('presence', { id: null, online: false, devices: [] });
      // not yet confirmed by the server: the confirmation would go to the dead socket
      $.each(pendingList(), function (i, p) {
        if (!p.delivered) failP(p, 'channel_offline', { uncertain: true });
      });
      // delivered requests keep waiting: the answer is addressed by device id and can reach the new socket
    },
    onStop: function (code) {
      $.each(pendingList(), function (i, p) {
        failP(p, code || 'stopped', { uncertain: p.delivered });
      });
      devices = {};
      selfId = null;
    },
    busy: function () {
      return !$.isEmptyObject(pending);
    },
  });

  T.relay = {
    send: function (to, topic, data) {
      return out(to == null ? '' : to, topic, data, false);
    },
    broadcast: function (topic, data) {
      return out(null, topic, data, false);
    },
    request: function (to, topic, data, opts) {
      return out(to == null ? '' : to, topic, data, true, opts && opts.timeout);
    },
    on: function (topic, fn) {
      handlers[topic] = fn;
    },
    off: function (topic) {
      delete handlers[topic];
    },
    devices: deviceList,
    isOnline: function (id) {
      return !!devices[String(id)];
    },
    self: function () {
      return selfId;
    },
  };
})(window, jQuery);
